import type { QueryReturnValue } from '@reduxjs/toolkit/query/react';

import type { AppError } from '../../utils/firebaseErrors';
import { buildReportsMapStaleReadKeys } from '../staleReadFallback/queryKeys';
import {
  clearSuccessfulRead,
  recordSuccessfulRead,
  resolveStaleReadFallback,
  type StaleReadMeta,
} from '../staleReadFallback/staleReadCache';
import {
  assertReportListQueryCapabilities,
  toCanonicalReportFilters,
} from './reportQueryCapabilities';
import { toReportInvalidQueryError } from './reportQueryErrors';
import { pruneReportPresenceLostMapPoints } from './reportReadFamilyPolicy';
import type { BackofficeCanonicalReportFilterItem, ReportMapPoint } from './reportsGateway';

type ReportsFilterItem = {
  field: string;
  op: string;
  value: string | string[];
};

const hasText = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

const cleanFilters = (filters: ReportsFilterItem[]) =>
  filters.filter(
    (item) =>
      hasText(item.field) &&
      hasText(item.op) &&
      item.value !== undefined &&
      item.value !== null &&
      item.value !== ''
  );

const addStaleReadMeta = <T>(data: T, staleRead: StaleReadMeta): T => {
  if (Array.isArray(data)) {
    return Object.assign([...data], { staleRead }) as T;
  }

  if (data && typeof data === 'object') {
    return Object.assign({}, data, { staleRead }) as T;
  }

  return data;
};

export const readReportMapFamily = async ({
  filters,
  read,
  isInvalidQueryFailure,
  toInvalidQueryError,
  toAppError,
}: {
  filters: ReportsFilterItem[];
  read: (filters: BackofficeCanonicalReportFilterItem[]) => Promise<ReportMapPoint[]>;
  isInvalidQueryFailure?: (error: unknown) => boolean;
  toInvalidQueryError?: (error: unknown) => AppError;
  toAppError?: (error: unknown) => AppError;
}): Promise<QueryReturnValue<ReportMapPoint[] & { staleRead?: StaleReadMeta }, AppError, {}>> => {
  const sanitizedFilters = cleanFilters(filters);
  let canonicalFilters: BackofficeCanonicalReportFilterItem[];
  try {
    assertReportListQueryCapabilities(sanitizedFilters);
    canonicalFilters = toCanonicalReportFilters(sanitizedFilters);
  } catch (error: unknown) {
    return {
      error: toInvalidQueryError ? toInvalidQueryError(error) : toReportInvalidQueryError(error),
    };
  }

  const staleReadKeys = buildReportsMapStaleReadKeys({ filters });

  try {
    const result = await read(canonicalFilters);
    const visibleResult = pruneReportPresenceLostMapPoints(result);
    recordSuccessfulRead(staleReadKeys.exactKey, staleReadKeys.familyKey, visibleResult);
    return { data: visibleResult };
  } catch (error: unknown) {
    if (isInvalidQueryFailure?.(error)) {
      return {
        error: toInvalidQueryError ? toInvalidQueryError(error) : toReportInvalidQueryError(error),
      };
    }

    const staleResult = resolveStaleReadFallback<ReportMapPoint[]>(
      staleReadKeys.exactKey,
      staleReadKeys.familyKey,
      false
    );

    if (staleResult) {
      return {
        data: addStaleReadMeta(
          pruneReportPresenceLostMapPoints(staleResult.data),
          staleResult.staleRead
        ),
      };
    }

    return { error: toAppError ? toAppError(error) : (error as AppError) };
  }
};

export const clearReportMapReadFamilyCache = (filters: ReportsFilterItem[]): void => {
  const staleReadKeys = buildReportsMapStaleReadKeys({ filters });
  clearSuccessfulRead(staleReadKeys.exactKey, staleReadKeys.familyKey);
};
